import { FormattedMessage, useIntl } from "react-intl";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { VerificationBanner } from "../auth/VerificationBanner";
import { SECTIONS } from "../core/sections";
import { BrandMark } from "../shared/Shared";
import { AccountMenu } from "./AccountMenu";
import { useAppContext } from "./AppContext";
import styles from "./AppShell.module.css";

interface NavigationItem {
  path: string;
  labelId: "nav.builder" | "nav.templates" | "nav.marketplace" | "nav.history" | "nav.billing";
  /** The section this destination belongs to; the item is hidden while that flag is off. */
  section?: keyof typeof SECTIONS;
}

// /dashboard IS the builder on both sites (no dashboard page) — label it that way.
const NAVIGATION: NavigationItem[] = [
  { path: "/dashboard", labelId: "nav.builder", section: "projects" },
  { path: "/templates", labelId: "nav.templates" },
  { path: "/marketplace", labelId: "nav.marketplace", section: "marketplace" },
  { path: "/history", labelId: "nav.history", section: "projects" },
  { path: "/billing", labelId: "nav.billing" },
];

// Section 1 gating: a destination whose API is not in this build gets no link (its route
// still renders the ComingNext panel for deep links). Returns with its section's flag.
const LIVE_NAVIGATION = NAVIGATION.filter((item) => !item.section || SECTIONS[item.section]);

function Navigation({ className }: { className: string }) {
  const intl = useIntl();
  return (
    <nav className={className} aria-label={intl.formatMessage({ id: "nav.primary" })}>
      {LIVE_NAVIGATION.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}
        >
          <FormattedMessage id={item.labelId} />
        </NavLink>
      ))}
    </nav>
  );
}

export function AppShell() {
  // Both sites use the V1 top-bar shell (LYA's sidebar shell retired with
  // the 2026-07 corporate rebuild); signed-in locale switching lives in the
  // AccountMenu.
  const intl = useIntl();
  const { experience } = useAppContext();
  const location = useLocation();
  // The builder route owns its whole top bar (owner design canvas): the shell
  // contributes only the skip link and the mobile bar there. While projects are
  // gated the route shows the ComingNext panel, which needs the ordinary chrome.
  const builderOwnsChrome = SECTIONS.projects && /^\/projects\//.test(location.pathname);
  return (
    <div className={styles.shell}>
      <a className={styles.skipLink} href="#main-content">
        <FormattedMessage id="nav.skip" />
      </a>
      <VerificationBanner />
      {builderOwnsChrome ? null : (
        <header className={styles.topbar}>
          {/* Brand = home (Templates + Start, where / lands); /dashboard would
              bounce into the builder via MfaBuilderGateway. */}
          <NavLink className={styles.brand} to="/templates">
            <BrandMark label={experience.brand.name} />
            <span className={styles.brandText}>{experience.brand.name}</span>
          </NavLink>
          <nav className={styles.nav} aria-label={intl.formatMessage({ id: "nav.primary" })}>
            {/* Builder + History arrive with projects (SECTIONS.projects). */}
            {SECTIONS.projects ? (
              <NavLink to="/dashboard" className={styles.builderPill}>
                <FormattedMessage id="nav.builder" />
              </NavLink>
            ) : null}
            <NavLink
              to="/templates"
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}
            >
              <FormattedMessage id="nav.templates" />
            </NavLink>
            {SECTIONS.projects ? (
              <NavLink
                to="/history"
                className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}
              >
                <FormattedMessage id="nav.history" />
              </NavLink>
            ) : null}
            <NavLink
              to="/billing"
              className={({ isActive }) => `${styles.navLink} ${isActive ? styles.active : ""}`}
            >
              <FormattedMessage id="publicNav.pricing" />
            </NavLink>
          </nav>
          <AccountMenu />
        </header>
      )}
      <main
        className={builderOwnsChrome ? styles.builderContent : styles.content}
        id="main-content"
      >
        <Outlet />
      </main>
      <Navigation className={styles.mobileBar} />
    </div>
  );
}
